import * as Sentry from '@sentry/react';
import axios, {
  AxiosError,
  AxiosResponse,
  CancelTokenSource,
} from 'axios';
import { Path } from 'path-parser';

import { store } from '@app/store';
import appConfig from '@config/app';
import { clearUserTokenAction } from '@reducers/auth/AuthActionConfig';

import MessagingService from './MessagingService';

const { whitelistUrl } = appConfig;

const configuredAxios = axios.create();

// Global cancel token source
let cancelSource: CancelTokenSource = axios.CancelToken.source();

// Global logout flag
let isLoggingOut = false;

export function cancelAllAxiosRequests(message = 'Request Cancelled') {
  cancelSource.cancel(message);
  // renew for next login
  cancelSource = axios.CancelToken.source();
}

export function startLogout() {
  isLoggingOut = true;
}

export function finishLogout() {
  isLoggingOut = false;
}

configuredAxios.defaults.withCredentials = true;

configuredAxios.defaults.headers.common = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
  'X-Requested-With': 'XMLHttpRequest',
};

configuredAxios.interceptors.request.use(
  // async (config) => config,
  async (config) => {
    const url = config.url || '';
    const newConfig = { ...config };
    // block request if logging out
    if (isLoggingOut && !url.includes('/logout')) {
      return Promise.reject(new axios.Cancel('Blocked during logout'));
    }

    newConfig.cancelToken = cancelSource.token;
    return newConfig;
  },
  (error) => Promise.reject(error),
);

function interceptSuccess(response: AxiosResponse) {
  // success interception logic here
  return response;
}

function isWhiteListed(url = '') {
  const pathname = url.split('/api')[1];
  return whitelistUrl.some((path) => new Path(path).test(pathname));
}

function interceptError(err: AxiosError) {
  // Ignore silently canceled requests
  if (axios.isCancel(err)) {
    return Promise.reject(err);
  }

  const { response } = err;

  // Log to Sentry with status code attached
  Sentry.captureException(err, {
    extra: {
      status: response?.status,
      responseData: response?.data,
      url: response?.config?.url,
      method: response?.config?.method,
    },
  });
  // connection refuse
  if (!response) {
    // eslint-disable-next-line no-console
    console.log('response', response);
  } else if (response.status === 401) { // unauthorized
    // remove the saved token and user data then proceed
    // on redirecting to root page (or login)
    // refresh the page and redirect to root page
    if (!isWhiteListed(response.config.url)) {
      // clear the messaging instance once the user logs out, to avoid
      // 401 issue in broadcasting when changing accounts (re-initialize getInstance())
      MessagingService.clearInstance();

      store.dispatch(clearUserTokenAction());

      // if (!err.config?.url?.endsWith('login')) window.location.href = '/auth/login';
      if (!err.config?.url?.includes('auth/login')) {
        // Capture the page the user is CURRENTLY on so an expired session
        // behind a deep link (e.g. an email /referral-messages/{id} link
        // opened days later) returns them there after re-login. Previously
        // this used a possibly-stale localStorage.lastPage, which landed
        // the user on Home. Skip auth pages, and fall back to lastPage /
        // plain login when there is no meaningful current path.
        const currentPath = window.location.pathname + window.location.search;
        const onAuthPage = window.location.pathname.startsWith('/auth');
        const returnTo = (!onAuthPage && currentPath && currentPath !== '/')
          ? currentPath
          : (localStorage.getItem('lastPage') ?? '');
        if (returnTo && returnTo !== '/') {
          localStorage.setItem('lastPage', returnTo);
          // Encode so a current path that itself carries a query string
          // (e.g. /referrals/pending?tab=x) is not truncated at the second
          // "?" when read back via URLSearchParams on the login page.
          window.location.href = `/auth/login?returnTo=${encodeURIComponent(returnTo)}`;
        } else {
          window.location.href = '/auth/login';
        }
      }
    }
  }

  return Promise.reject(err);
}

// interceptors
configuredAxios.interceptors.response.use(interceptSuccess, interceptError);

export default configuredAxios;
